// ============================================================================
// Doc Order — lender-credit floor / ledger / escalation predicate ONLY.
// Capacity math is NOT here: the live P&P cap model is the law —
// `_shared/pricing-cap-math.ts` computeLenderCreditCaps (+ the projected
// below-par helper) with inputs from live concessions / applied LOCR / LENPCC
// and the active delivery-lock basis. maxAllowed4794 = min(generic capacity,
// below-par-safe 4794); headroom = maxAllowed4794 − current 4794. See the
// approved plan ("LO compensation & lender-credit capacity") and LW26052462.
// ============================================================================

/**
 * Increase-only floor (README §2, Field 4794): the credit may increase but
 * never drop below what the last LE disclosed. Floor source is a flag-ON gate
 * (v1 seeds it from 4794 at first pull — ops decision pending).
 */
export function lenderCreditBelowFloor(
  requested: number | null | undefined,
  disclosedFloor: number | null | undefined
): boolean {
  const req = Number(requested);
  const floor = Number(disclosedFloor);
  if (!Number.isFinite(req) || !Number.isFinite(floor) || floor <= 0) return false;
  return req < floor - 0.005;
}

/** Over-capacity ⇒ the Apply action is replaced by Escalate-to-lock-desk (E3). */
export function lenderCreditExceedsCapacity(
  requested: number | null | undefined,
  maxAllowed4794: number | null | undefined
): boolean {
  const req = Number(requested);
  const cap = Number(maxAllowed4794);
  if (!Number.isFinite(req) || req <= 0) return false;
  if (!Number.isFinite(cap)) return true; // no capacity basis (e.g. no lock) → escalate, never apply
  return req > cap + 0.005;
}

/**
 * Cash ledger (spec A1.9): Field 142 is + = cash FROM borrower, − = TO.
 * Applying a lender credit reduces what the borrower brings in.
 */
export function cashLedgerAfterCredit(
  cashToFromBorrower: number | null | undefined,
  lenderCredit: number | null | undefined
): number {
  return (Number(cashToFromBorrower) || 0) - (Number(lenderCredit) || 0);
}
